import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { getCurrentLanguage } from '@protector/utils/i18n';
import { AUTH_SECRET_ID, CORE_SERVICES_API_URL } from '../../config/constants.ts';
import type { HeadersParams } from '../http-client.types.ts';
import { getRefreshToken } from './auth.functions.ts';
import { redirectToUnlicensed } from './refresh.functions.ts';

interface Token {
  access_token: string;
  refresh_token: string;
}

async function refreshAccessToken(token: string): Promise<AxiosResponse<Token>> {
  const body = new URLSearchParams();
  body.append('grant_type', 'refresh_token');
  body.append('refresh_token', token);

  return axios.post<Token>(`${CORE_SERVICES_API_URL}/oauth/token`, body, {
    headers: {
      Authorization: `Basic ${AUTH_SECRET_ID}`,
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  });
}

interface TryRefreshAndRetryProps {
  callbackError: (error: Error) => Promise<never>;
  originalError: Error;
  headers: HeadersParams;
  config: AxiosRequestConfig;
}

let refreshPromise: Promise<string> | null = null;

function startRefresh(refresh_token: string): Promise<string> {
  sessionStorage.setItem('refreshed', 'true');
  return refreshAccessToken(refresh_token)
    .then(response => {
      const { access_token, refresh_token: newRefreshToken } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
      sessionStorage.removeItem('refreshed');
      return access_token;
    })
    .finally(() => {
      refreshPromise = null;
    });
}

export const tryRefreshAndRetry = async ({ callbackError, originalError, headers, config }: TryRefreshAndRetryProps) => {
  if (config.refreshAttempted) {
    redirectToUnlicensed();
    sessionStorage.setItem('unlicensed', 'true');
    throw originalError;
  }

  if (sessionStorage.getItem('refreshed') && !refreshPromise) {
    redirectToUnlicensed();
    sessionStorage.setItem('unlicensed', 'true');
    sessionStorage.removeItem('refreshed');
    throw originalError;
  }

  const refresh_token = getRefreshToken();
  if (!refresh_token) throw originalError;

  try {
    refreshPromise ??= startRefresh(refresh_token);
    const access_token = await refreshPromise;

    const retriedRequest: AxiosRequestConfig = {
      ...config,
      refreshAttempted: true,
      headers: {
        ...headers,
        'Accept-Language': getCurrentLanguage(),
        Authorization: `Bearer ${access_token}`
      }
    };
    return axios.request(retriedRequest);
  } catch (error) {
    redirectToUnlicensed();
    sessionStorage.setItem('unlicensed', 'true');
    return callbackError(error as Error);
  }
};
